import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChartLine, Trash2, MoreVertical, Pencil } from "lucide-react";
import { createColumnHelper } from "@tanstack/react-table";
import type { Dashboard } from "@/types/dashboard";
import { CreateDashboardModal } from "./CreateDashboardModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DataTable } from "@/components/DataTable";
import { useDashboardStore } from "@/hooks/useDashboardStore";

const col = createColumnHelper<Dashboard>();

export default function DashboardsPage() {
  const navigate = useNavigate();
  const { dashboards, createDashboard, deleteDashboard } = useDashboardStore();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  async function handleCreate(name: string, description?: string) {
    const config = await createDashboard(name, description);
    navigate(`/admin/dashboards/${config.slug}/edit`);
  }

  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Name",
        cell: (info) => (
          <div className="flex items-center gap-2.5 min-w-0">
            <ChartLine className="h-4 w-4 text-brand-cyan shrink-0" />
            <p className="text-sm font-medium text-primary truncate">{info.getValue()}</p>
          </div>
        ),
      }),
      col.accessor("status", {
        header: "Status",
        size: 120,
        cell: (info) => {
          const s = info.getValue() ?? "draft";
          return s === "available" ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-cyan/15 text-brand-cyan">
              Available
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-amber/15 text-accent-amber">
              Draft
            </span>
          );
        },
      }),
      col.accessor("updated_at", {
        header: "Last modified",
        size: 180,
        cell: (info) => (
          <span className="text-sm text-primary/70">
            {new Date(info.getValue()).toLocaleString("sv-SE", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        ),
      }),
      col.display({
        id: "actions",
        header: "",
        size: 48,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1.5 rounded text-muted hover:text-primary hover:bg-surface-elevated transition-colors cursor-pointer"
                  title="Actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-40">
                <DropdownMenuItem
                  onClick={() => navigate(`/admin/dashboards/${row.original.slug}/edit`)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteTarget(row.original.id)}
                  className="text-rose-400 focus:text-rose-400 focus:bg-rose-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      }),
    ],
    [navigate],
  );

  return (
    <>
      <div className="w-full py-6 px-4">
        <DataTable
          data={dashboards}
          columns={columns}
          title="Dashboards"
          searchPlaceholder="Search dashboards…"
          emptyMessage="No dashboards yet. Create your first one."
          onRowClick={(dash) => navigate(`/admin/dashboards/${dash.slug}/edit`)}
          actions={<Button onClick={() => setCreateOpen(true)}>New Dashboard</Button>}
        />
      </div>
      <CreateDashboardModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete dashboard"
        description="This will permanently remove the dashboard and all its sections. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) await deleteDashboard(deleteTarget);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}
